<template>
    <div class="aggregation-rule">
        <template v-if="!isLocaleEnglish">
            <div class="flex-break" />
            <strong>{{ $t('Criteria') }}</strong>
            <div class="flex-break" />
        </template>
        <simple-select
            :options="operatorOptions"
            :value="operatorValue"
            :check-valid-rule="checkValidRule"
            :disabled="readOnly"
            preselect-first
            field="operator"
            @input="$emit('input', $event)" />
        <number-input
            field="value"
            :value="rule.value"
            :read-only="readOnly"
            :check-valid-rule="checkValidRule"
            :min="0"
            :invalid="numberValueInvalid"
            @input="$emit('input', $event)" />
        <div
            v-if="!isLocaleEnglish"
            class="flex-break" />
        <simple-select
            :options="timeOptions"
            :value="timeValue"
            :disabled="readOnly"
            :check-valid-rule="checkValidRule"
            preselect-first
            field="time"
            @input="$emit('input', $event)" />
        <component
            :is="dateComponent.component"
            v-if="dateComponent"
            v-bind="dateComponent.props"
            :read-only="readOnly"
            :check-valid-rule="checkValidRule"
            @input="$emit('input', $event)" />
        <div
            v-if="showEventPropertyFilterSection"
            class="aggregation-rule-section aggregation-rule-section--event-property">
            <div
                v-if="rule.useEventProperty"
                class="aggregation-rule__event-property-divider">
                <div class="aggregation-rule__event-property-label">
                    {{ $t('ruleEditor.WITH EVENT PROPERTY') }}
                </div>
                <pendo-divider
                    decorative
                    stroke="var(--palette-gray-40)"
                    stroke-dasharray="2" />
            </div>
        </div>
        <event-property-filter-section
            v-if="showEventPropertyFilterSection"
            :filters="eventPropertyFilters"
            :target-entities="[selectedResource]"
            style-type="segment"
            :disabled="readOnly"
            :enable-visitor-account-metadata="false"
            :include-page-parameter="includePageParameter"
            :include-location-data="includeLocationData"
            :include-server-data="includeServerData"
            :metadata="metadata"
            :historical-metadata="historicalMetadata"
            :session-properties="sessionProperties"
            :is-web-analytics="false"
            :max-filters="1"
            :add-filter-menu-open="isAddFilterMenuOpen"
            @save="saveFilter"
            @delete="deleteFilter"
            @filter-complete="onFilterComplete" />
    </div>
</template>

<script>
import { PendoDivider } from '@pendo/components';
import NumberInput from '@/segments/components/segment-editor-vue/components/number-input.vue';
import AbsoluteDateRange from '@/segments/components/segment-editor-vue/components/absolute-date-range.vue';
import RelativeDateRange from '@/segments/components/segment-editor-vue/components/relative-date-range.vue';
import SimpleSelect from '@/segments/components/segment-editor-vue/components/simple-select.vue';
import { FilterType, MetadataTypeId } from '@/types/analytics/analytics.types';
import EventPropertyFilterSection from '@/common/event-property-filter/event-property-filter-section.vue';
import { convertOperatorToFilterOperator, convertFilterOperatorToOperator } from '@/common/event-property-filter/utils';
import {
    getTimeOptions,
    getRelativeDateRangeConfig,
    getAbsoluteDateRangeConfig,
    getOperatorOptions,
    getResource,
} from '@/segments/components/segment-editor-vue/segment-editor.utils';
import isArray from 'lodash/isArray';

export default {
    name: 'AggregationRule',
    components: {
        NumberInput,
        AbsoluteDateRange,
        RelativeDateRange,
        SimpleSelect,
        EventPropertyFilterSection,
        PendoDivider,
    },
    props: {
        rule: {
            type: Object,
            required: true,
        },
        config: {
            type: Object,
            required: true,
        },
        resources: {
            type: Array,
            default: () => [],
        },
        checkValidRule: {
            type: Boolean,
            default: false,
        },
        readOnly: {
            type: Boolean,
            default: false,
        },
        isLocaleEnglish: {
            type: Boolean,
            default: true,
        },
        includePageParameter: {
            type: Boolean,
            default: false,
        },
        includeLocationData: {
            type: Boolean,
            default: false,
        },
        includeServerData: {
            type: Boolean,
            default: false,
        },
        metadata: {
            type: Object,
            default: () => ({}),
        },
        historicalMetadata: {
            type: Object,
            default: () => ({}),
        },
        sessionProperties: {
            type: Object,
            default: () => ({}),
        },
    },
    emits: ['input', 'bulk-input'],
    data () {
        return {
            timeOptions: [],
            isAddFilterMenuOpen: false,
            lastAddedFilterId: null,
        };
    },
    computed: {
        operatorOptions () {
            return getOperatorOptions({ schema: this.rule.schema, id: this.rule.id });
        },
        operatorValue () {
            return this.operatorOptions.find((option) => option.operator === this.rule.operator);
        },
        timeValue () {
            return this.timeOptions.find((option) => option.time === this.rule.time);
        },
        dateComponent () {
            return {
                withinLast: getRelativeDateRangeConfig(this.rule),
                between: getAbsoluteDateRangeConfig(this.rule),
            }[this.rule.time];
        },
        selectedResource () {
            const resource = getResource(this.rule, this.resources);
            if (resource) {
                if (resource.kind === 'Page') {
                    return { ...resource, type: 'pageViews' };
                }
                if (resource.kind === 'Feature') {
                    return { ...resource, type: 'featureClicks' };
                }
                if (resource.kind === 'TrackType') {
                    return { ...resource, type: 'trackEvents' };
                }
                if (resource.kind === 'Guide') {
                    return { ...resource, type: 'guideViews' };
                }

                return resource;
            }

            return null;
        },
        showEventPropertyFilterSection () {
            if (!this.selectedResource) {
                return false;
            }

            let isEventPropertyRule = false;
            if (!this.config.eventProperties) {
                isEventPropertyRule = false;
            } else if (this.rule.id === 'feature' || this.rule.id === 'trackType' || this.rule.id === 'page') {
                isEventPropertyRule = true;
            } else {
                isEventPropertyRule = false;
            }

            if (isEventPropertyRule) {
                return this.eligibleForEventPropertyRules;
            }

            return false;
        },
        eligibleForEventPropertyRules () {
            return Boolean(this.rule.fact && this.rule.schema === 'aggregation' && this.rule.kind === 'visitor');
        },
        eventPropertyFilters () {
            const filters = [];
            const {
                useEventProperty,
                eventPropertyField,
                eventPropertyKind,
                eventPropertyGroup,
                eventPropertySchema,
                eventPropertyValue,
                eventPropertyOperator,
                filterType,
            } = this.rule;

            const filterOperator = convertOperatorToFilterOperator(
                eventPropertyOperator,
                eventPropertySchema,
                eventPropertyValue,
            );

            if (useEventProperty) {
                const filter = {
                    field: eventPropertyField,
                    kind: eventPropertyKind,
                    group: eventPropertyGroup,
                    schema: eventPropertySchema,
                    operator: filterOperator,
                    comparisonValues: isArray(eventPropertyValue) ? eventPropertyValue : [eventPropertyValue],
                    filterType,
                };
                if (filterType === FilterType.Custom && eventPropertyKind === undefined) {
                    filter.kind = MetadataTypeId.Event;
                }
                filters.push(filter);
            }

            return filters;
        },
        numberValueInvalid () {
            return this.rule.id === 'trackType' && this.rule.value === 0;
        },
    },
    watch: {
        eligibleForEventPropertyRules (eligibleNew, eligibleOld) {
            if (!eligibleNew && eligibleOld) {
                this.$emit('input', { field: 'useEventProperty', value: false });
            }
        },
        showEventPropertyFilterSection (showNew, showOld) {
            if (!showNew && showOld) {
                this.$emit('input', { field: 'useEventProperty', value: false });
            }
        },
    },
    created () {
        this.timeOptions = getTimeOptions().aggregation;
    },
    methods: {
        saveFilter ({ filter }) {
            this.isAddFilterMenuOpen = true;
            if (filter.id) {
                this.lastAddedFilterId = filter.id;
            }
            const operatorValues = convertFilterOperatorToOperator(filter.operator);
            const updates = {};
            updates.useEventProperty = true;
            updates.eventPropertyField = filter.field;
            updates.eventPropertyKind = filter.kind;
            updates.eventPropertyGroup = filter.group;
            updates.eventPropertyOperator = operatorValues?.operator ?? filter.operator;
            updates.automaticEventProperty = filter.kind === MetadataTypeId.Automatic;
            updates.filterType = filter.filterType;
            if (filter.filterType === FilterType.Custom) {
                updates.eventPropertyKind = MetadataTypeId.Event;
            }
            updates.eventPropertyValue =
                operatorValues?.value !== undefined ? [operatorValues.value] : filter.comparisonValues;
            this.$emit('bulk-input', updates);
        },
        deleteFilter () {
            this.isAddFilterMenuOpen = false;
            this.lastAddedFilterId = null;
            const updates = {};
            updates.automaticEventProperty = null;
            updates.useEventProperty = false;
            updates.eventPropertyField = null;
            updates.eventPropertyKind = null;
            updates.eventPropertyGroup = null;
            updates.eventPropertySchema = null;
            updates.eventPropertyValue = null;
            updates.eventPropertyOperator = null;
            updates.eventPropertyName = null;
            updates.name = null;
            updates.filterType = null;
            this.$emit('bulk-input', updates);
        },
        onFilterComplete ({ filter }) {
            if (filter.id === this.lastAddedFilterId || !this.lastAddedFilterId) {
                this.isAddFilterMenuOpen = false;
                this.lastAddedFilterId = null;
            }
        },
    },
};
</script>

<style scoped lang="less">
.aggregation-rule {
    display: contents;
}

.flex-break {
    /* Reliable flex line break - forces next items to new line */
    flex-basis: 100%;
    height: 0;
    width: 0;
    overflow: hidden;
}

.aggregation-rule__event-property-label {
    font-weight: 600;
    color: var(--palette-gray-70);
    white-space: nowrap;
}

.aggregation-rule__event-property-divider {
    display: flex;
    align-items: center;
    padding: 0 16px;
    width: 100%;

    .pendo-divider {
        margin-left: 8px;
    }
}

.aggregation-rule-section {
    display: contents;
}
</style>
